import { useState, useEffect } from 'react';
import { Line } from "react-chartjs-2";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
} from "reactstrap";
import { Grid, Heading, View, SwitchField } from '@aws-amplify/ui-react';

import { fetchSummary, getFortnightPeriods, getLastFortnight, fetchOrganic } from './Dev';

import './css/dashboard.css';
import './css/Graphs.css';


// Organic country codes the fetchOrganic lambda accepts (US is "NA").
const ORGANIC_CODES = ["NA", "UK", "DE", "FR", "IT", "ES"];

// The three metric graphs per row. `adv` is the advertised-summary field that
// corresponds to each organic metric (sales->sales, sessions->clicks, units->purchases).
const METRICS = [
    { key: "sales", title: "Sales", adv: "sales" },
    { key: "sessions", title: "Sessions", adv: "clicks" },
    { key: "units", title: "Units Ordered", adv: "purchases" },
];

// One row per region. `organic` = organic country codes to sum; `adv` = advertised
// summary regions to sum. The EU row can additionally break out its countries as
// extra lines on its own graphs (toggled in its heading).
const ROWS = [
    { key: "World", flag: "🌍", label: "World", organic: ORGANIC_CODES, adv: ["US", "EU", "UK"] },
    { key: "US", flag: "🇺🇸", label: "US", organic: ["NA"], adv: ["US"] },
    { key: "UK", flag: "🇬🇧", label: "UK", organic: ["UK"], adv: ["UK"] },
    { key: "EU", flag: "🇪🇺", label: "EU", organic: ["DE", "FR", "IT", "ES"], adv: ["EU"], euBreakdown: true },
];

// Per-country organic lines shown on the EU graphs when the toggle is on. Colours
// avoid clashing with the "All" (orange) and "Advertised" (green) lines.
const EU_COUNTRIES = [
    { code: "DE", label: "🇩🇪 Germany", color: "#2563EB" },
    { code: "FR", label: "🇫🇷 France", color: "#9333EA" },
    { code: "IT", label: "🇮🇹 Italy", color: "#DB2777" },
    { code: "ES", label: "🇪🇸 Spain", color: "#0891B2" },
];

const ORGANIC_COLOR = "#FF5722"; // "All" (total ordered product sales)
const ADVERTISED_COLOR = "#4CAF50"; // "Advertised"

// Sum a country's daily organic rows into the fortnight buckets (start inclusive,
// end exclusive), combining standard + B2B for each metric.
const bucketIntoFortnights = (dailyData, periods) => {
    const data = dailyData || [];
    return periods.map((period) => {
        const [start, end] = period.split(' to '); // "YYYY-MM-DD to YYYY-MM-DD"
        const inBucket = data.filter((item) => item.date >= start && item.date < end);
        const sum = (fn) => inBucket.reduce((acc, item) => acc + fn(item), 0);
        return {
            sales: sum((i) => (i.sales || 0) + (i.salesB2B || 0)),
            sessions: sum((i) => (i.sessions || 0) + (i.sessionsB2B || 0)),
            units: sum((i) => (i.unitsOrdered || 0) + (i.unitsOrderedB2B || 0)),
        };
    });
};


const GlobalDashboard = ({ company }) => {
    const [summaryData, setSummaryData] = useState(undefined);
    // organic = { [countryCode]: fortnightly[] }, each fortnightly[] aligned to `fortnights`.
    const [organic, setOrganic] = useState(null);
    const [showEuCountries, setShowEuCountries] = useState(false);

    const formattedEnd = getLastFortnight();
    const fortnights = getFortnightPeriods(formattedEnd);
    const formattedStart = fortnights[0].slice(0, 10);

    useEffect(() => {
        if (!company) {
            return;
        }
        fetchSummary(company)
            .then(setSummaryData)
            .catch((error) => console.error('Error fetching summary:', error));
    }, [company]);

    useEffect(() => {
        if (!company) {
            return;
        }
        const loadOrganic = async () => {
            try {
                // Fetch each country daily and bucket into fortnights. A country with no
                // data in range makes the lambda error, so default those to empty.
                const results = await Promise.all(
                    ORGANIC_CODES.map((code) =>
                        fetchOrganic(code, formattedStart, formattedEnd, "day").catch(() => [])
                    )
                );
                const byCode = {};
                ORGANIC_CODES.forEach((code, idx) => {
                    byCode[code] = bucketIntoFortnights(results[idx], fortnights);
                });
                setOrganic(byCode);
            } catch (error) {
                console.error('Error fetching organic data:', error);
            }
        };
        loadOrganic();
    }, [company]);

    const chart_options = {
        responsive: true,
        maintainAspectRatio: true,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { display: true, position: 'bottom' },
        },
        scales: {
            y: { beginAtZero: true },
        },
    };

    const labels = fortnights.map((period) => period.split(' to ')[0]);

    const lineStyle = (label, color, data) => ({
        label,
        data,
        borderColor: color,
        backgroundColor: color,
        fill: false,
        tension: 0.3,
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 4,
    });

    const organicSeries = (codes, metricKey) =>
        fortnights.map((_, i) => codes.reduce((sum, code) => sum + (organic[code]?.[i]?.[metricKey] || 0), 0));

    // Build the (All organic) + optional (Advertised) + optional (EU country) datasets.
    const buildChartData = (row, metric) => {
        if (!organic) {
            return { labels: [], datasets: [] };
        }

        const datasets = [lineStyle("All", ORGANIC_COLOR, organicSeries(row.organic, metric.key))];

        if (row.adv.length) {
            const advData = fortnights.map((_, i) =>
                row.adv.reduce(
                    (sum, region) => sum + (summaryData?.data?.total?.[region]?.[metric.adv]?.[i] || 0),
                    0
                )
            );
            datasets.push(lineStyle("Advertised", ADVERTISED_COLOR, advData));
        }

        if (row.euBreakdown && showEuCountries) {
            EU_COUNTRIES.forEach((c) => {
                datasets.push(lineStyle(c.label, c.color, organicSeries([c.code], metric.key)));
            });
        }

        return { labels, datasets };
    };

    return (
        <View>
            {ROWS.map((row) => (
                <View key={row.key} marginBottom="1.5rem">
                    <View style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '0.5rem' }}>
                        <Heading level={4} marginBottom="0">{row.flag} {row.label}</Heading>
                        {row.euBreakdown && (
                            <SwitchField
                                label="Show countries"
                                labelPosition="end"
                                isChecked={showEuCountries}
                                onChange={(e) => setShowEuCountries(e.target.checked)}
                            />
                        )}
                    </View>
                    <Grid
                        columnGap="0.5rem"
                        rowGap="0.5rem"
                        templateColumns="1fr 1fr 1fr"
                        templateRows="1fr"
                    >
                        {METRICS.map((metric) => (
                            <Card className="card-chart" key={metric.key}>
                                <CardHeader>
                                    <Row>
                                        <Col className="text-left" sm="6">
                                            <CardTitle tag="h3">{metric.title}</CardTitle>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="chart-area">
                                        <Line
                                            data={buildChartData(row, metric)}
                                            options={chart_options}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                        ))}
                    </Grid>
                </View>
            ))}
        </View>
    );
}

export { GlobalDashboard };
